import * as React from 'react'

import {
  Button as ProfylrButton,
  type ButtonProps as ProfylrButtonProps,
  buttonVariants as profylrButtonVariants,
} from '@profylr/ui'
import { cn } from '@/lib/utils'

/**
 * Adapter: the shadcn Button's vocabulary, rendered by @profylr/ui's Button.
 *
 * The design system has five variants and no coloured ones -- section 3.7:
 * actions run on purple in every app, and a "success" or "warning" button
 * puts RAG colour on chrome, which the accent-on-data rule exists to stop.
 * The legacy names map onto the nearest role here so the 77 importing files
 * move at once; the per-file sweep retires the names.
 */
export type LegacyButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'text'
  | 'success'
  | 'warning'
  | 'rounded'

export type LegacyButtonSize = 'default' | 'sm' | 'lg' | 'icon' | 'xl' | 'md'

const VARIANTS: Record<LegacyButtonVariant, NonNullable<ProfylrButtonProps['variant']>> = {
  default: 'primary',
  destructive: 'destructive',
  outline: 'secondary',
  secondary: 'secondary',
  ghost: 'ghost',
  link: 'link',
  text: 'ghost',
  success: 'primary',
  warning: 'primary',
  rounded: 'primary',
}

// Legacy "icon" was 40px -- the height of the input it usually sits beside --
// so it maps to the control-height square, not the 32px standalone icon.
const SIZES: Record<LegacyButtonSize, NonNullable<ProfylrButtonProps['size']>> = {
  default: 'default',
  sm: 'sm',
  lg: 'lg',
  icon: 'icon-control',
  xl: 'lg',
  md: 'default',
}

export interface ButtonProps extends Omit<ProfylrButtonProps, 'variant' | 'size'> {
  variant?: LegacyButtonVariant | null
  size?: LegacyButtonSize | null
}

function mapVariant(variant?: LegacyButtonVariant | null) {
  return VARIANTS[variant ?? 'default']
}
function mapSize(size?: LegacyButtonSize | null) {
  return SIZES[size ?? 'default']
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, className, ...props }, ref) => (
    <ProfylrButton
      ref={ref}
      variant={mapVariant(variant)}
      size={mapSize(size)}
      className={cn(variant === 'rounded' && 'rounded-pill', className)}
      {...props}
    />
  ),
)
Button.displayName = 'Button'

/** For the few places that style another element as a button. */
function buttonVariants(
  opts: { variant?: LegacyButtonVariant | null; size?: LegacyButtonSize | null; className?: string } = {},
): string {
  return cn(
    profylrButtonVariants({ variant: mapVariant(opts.variant), size: mapSize(opts.size) }),
    opts.variant === 'rounded' && 'rounded-pill',
    opts.className,
  )
}

export { Button, buttonVariants }
